import React, { useState, useEffect, useMemo, useRef, ReactElement } from "react";
import { useParams, useNavigate, useLocation, Link, Navigate } from "react-router-dom";
import { Box, Drawer, List, ListItem, ListItemButton, ListItemText, ListItemIcon, Typography, Divider, IconButton, Tooltip, Stack, useTheme, useMediaQuery, Paper, Collapse } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessIcon from "@mui/icons-material/Business";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CategoryIcon from "@mui/icons-material/Category";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ScienceIcon from "@mui/icons-material/Science";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AddIcon from "@mui/icons-material/Add";
import LogoutIcon from "@mui/icons-material/Logout";
import { auth, db } from "../firebase";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { Workspace } from "../types";
import { isDemoMode } from "../utils/demoMode";
import { isBypassAuthEnabled } from "../utils/bypassAuth";
import { isDevModeEnabled, getDevModeType, setDevModeType, type DevModeType } from "../utils/devModeType";
import { clearWorkspaceContext } from "../utils/tabContext";
import { getVersion, formatVersion } from "../utils/version";
import { Alert, Card, CardContent, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Button } from "@mui/material";
import { signOut } from "firebase/auth";
import CurrencySettings from "./CurrencySettings";
import AccountSettings from "./AccountSettings";
import WorkspaceSettings from "./WorkspaceSettings";
import PermissionsSettings from "./PermissionsSettings";
import CategorySettings from "./CategorySettings";
import ManageDetailsPage from "./CategorySettings/ManageDetailsPage";
import SelectDetailsPage from "./CategorySettings/SelectDetailsPage";
import TestPage from "./TestPage";
import MembersAndPermissionsSettings from "./MembersAndPermissionsSettings";
import LoadingSpinner from "../components/LoadingSpinner";
import { useWorkspaceMembers } from "./WorkspaceSettings/hooks/useWorkspaceMembers";
import { useWorkspaceSettings } from "./WorkspaceSettings/hooks/useWorkspaceSettings";
import { useWorkspaceHelpers } from "./WorkspaceSettings/hooks/useWorkspaceHelpers";
import { useWorkspace } from "./WorkspaceSettings/hooks/useWorkspace";
import { useCategories } from "./CategorySettings/hooks/useCategories";
import { useCategoryGroups } from "./CategorySettings/hooks/useCategoryGroups";

interface SettingsProps {
  workspaceId: string;
  onBack?: () => void;
  onTestTermsDialog?: () => void;
  onOpenWorkspaceMenu?: (anchorEl: HTMLElement | null) => void;
  onOpenCreateWorkspaceDialog?: () => void;
  onLogout?: () => Promise<void>;
}

type SettingsPage = "currencies" | "accounts" | "workspace" | "workspace-information" | "workspace-transactions" | "workspace-preferences" | "workspace-danger" | "members-permissions" | "permissions" | "transaction-fields";

/** Eager-load categories/groups into cache when on Settings but not on Categories, so opening Categories shows data immediately. */
function CategoriesEagerPreload({ workspaceId }: { workspaceId: string }): null {
  useCategories({ workspaceId, type: "expense" });
  useCategories({ workspaceId, type: "income" });
  useCategoryGroups({ workspaceId, type: "expense" });
  useCategoryGroups({ workspaceId, type: "income" });
  return null;
}

/** Loads categories + groups into cache first; only then renders CategorySettings so it shows as already loaded. */
function CategoriesGate({ workspaceId }: { workspaceId: string }): ReactElement {
  const { loading: loadingCatExpense } = useCategories({ workspaceId, type: "expense" });
  const { loading: loadingCatIncome } = useCategories({ workspaceId, type: "income" });
  const { loading: loadingGrpExpense } = useCategoryGroups({ workspaceId, type: "expense" });
  const { loading: loadingGrpIncome } = useCategoryGroups({ workspaceId, type: "income" });
  const loading = loadingCatExpense || loadingCatIncome || loadingGrpExpense || loadingGrpIncome;
  if (loading) return <LoadingSpinner />;
  return <CategorySettings workspaceId={workspaceId} />;
}

function Settings({ workspaceId, onBack, onTestTermsDialog, onOpenWorkspaceMenu, onOpenCreateWorkspaceDialog, onLogout }: SettingsProps): ReactElement {
  const accountRowRef = useRef<HTMLDivElement | null>(null);
  const [accountExpanded, setAccountExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const pathParts = location.pathname.split('/').filter(Boolean);
  const settingsIndex = pathParts.indexOf('settings');
  const currentPage = pathParts.length > settingsIndex + 1 ? pathParts[settingsIndex + 1] : 'workspace';
  const atSettingsRoot = pathParts.length === settingsIndex + 1;

  const [isOwner, setIsOwner] = useState<boolean>(false);
  const [devModeType, setDevModeTypeState] = useState<DevModeType>(getDevModeType());
  // Get current mode to compare - use useMemo to make it reactive
  const currentDevModeType = useMemo(() => getDevModeType(), []);

  const drawerWidth = 240;

  // Prevent menu item labels from truncating (W..., C...) in narrow sidebars
  const menuItemTextProps = {
    primaryTypographyProps: {
      sx: { whiteSpace: "normal", overflow: "visible", textOverflow: "unset" },
    },
  };

  // Get workspace data
  const {
    workspace,
    isOwner: workspaceIsOwner,
  } = useWorkspace({ workspaceId });

  // Get current user role
  const {
    currentUserRole,
  } = useWorkspaceMembers({ workspaceId, workspace });

  // Get permissions
  const {
    permissions,
  } = useWorkspaceSettings({ workspaceId });

  const mobileSectionTitle: Record<string, string> = {
    workspace: "Workspace",
    "workspace-information": "Workspace",
    "workspace-transactions": "Transactions",
    "workspace-preferences": "Preferences",
    currencies: "Currencies",
    accounts: "Accounts",
    categories: "Categories",
    "manage-details": "Manage Details",
    "select-details": "Select Details",
    "members-permissions": "Members & permissions",
    test: "Test Page",
  };

  const isManageOrSelectDetailsPage = currentPage === "manage-details" || currentPage === "select-details";

  const workspaceSectionPages = ["workspace-information", "workspace-transactions", "workspace-preferences"] as const;
  const isWorkspaceSectionPage = (p: string): p is (typeof workspaceSectionPages)[number] => workspaceSectionPages.includes(p as any);
  const workspaceSectionFromPage = (p: string): "information" | "transactions" | "members" | "preferences" | "danger" => {
    if (p === "workspace-information") return "information";
    if (p === "workspace-transactions") return "transactions";
    if (p === "workspace-preferences") return "preferences";
    return "information";
  };

  // Check if user can manage workspace config (includes owners and admins)
  const { canManageWorkspaceConfig } = useWorkspaceHelpers({
    isOwner: workspaceIsOwner,
    currentUserRole,
    permissions,
  });

  // Check if current user is owner (for backward compatibility)
  useEffect(() => {
    // In demo mode, user is not owner (read-only access)
    if (isDemoMode()) {
      setIsOwner(false);
      return;
    }

    // In bypass auth mode, allow access without authentication
    if (isBypassAuthEnabled()) {
      // In bypass mode, treat as non-owner (read-only access)
      setIsOwner(false);
      return;
    }

    const user = auth.currentUser;
    if (!user) return;

    const workspaceRef = doc(db, "workspaces", workspaceId);
    const unsubscribe = onSnapshot(workspaceRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setIsOwner(user.uid === data.ownerId);
      }
    });

    return () => unsubscribe();
  }, [workspaceId]);

  // ——— Mobile layout: menu list or content with back header ———
  if (isMobile) {
    if (atSettingsRoot) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "background.default", overflow: "hidden" }}>
          <Paper
            elevation={0}
            sx={{
              flexShrink: 0,
              borderBottom: 1,
              borderColor: "divider",
              borderRadius: 0,
              bgcolor: "background.paper",
              pt: "env(safe-area-inset-top, 0px)",
              minHeight: 56,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              px: 0,
              py: 0,
            }}
          >
            <Box
              component={Link}
              to={`/${workspaceSlug}/activity`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                textDecoration: "none",
                color: "text.primary",
                minHeight: 56,
                pl: 1,
                pr: 1.5,
                "&:hover": { bgcolor: "action.hover" },
                "&:active": { bgcolor: "action.selected" },
                flexShrink: 0,
              }}
            >
              <ArrowBackIcon fontSize="medium" sx={{ fontSize: 24 }} />
              <Typography variant="body1" fontWeight="600" sx={{ fontSize: "1rem" }}>
                Back to App
              </Typography>
            </Box>
          </Paper>
          <List sx={{ flex: 1, overflow: "auto", py: 1, px: 1, bgcolor: "background.paper" }}>
            {(onOpenWorkspaceMenu || onOpenCreateWorkspaceDialog || onLogout) && (
              <>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    ref={accountRowRef}
                    onClick={() => setAccountExpanded((prev) => !prev)}
                    sx={{ py: 1.5, px: 1.5, borderRadius: 1, minHeight: 52 }}
                  >
                    <ListItemIcon sx={{ minWidth: 44, color: "primary.main" }}>
                      <BusinessIcon fontSize="medium" />
                    </ListItemIcon>
                    <ListItemText
                      primary={workspace?.name || "Account"}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: "1rem", ...menuItemTextProps.primaryTypographyProps }}
                    />
                    <Box
                      component="span"
                      sx={{
                        color: "text.secondary",
                        flexShrink: 0,
                        transform: accountExpanded ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                        display: "inline-block",
                      }}
                    >
                      ▼
                    </Box>
                  </ListItemButton>
                </ListItem>
                <Collapse in={accountExpanded} timeout="auto" unmountOnExit>
                  <List disablePadding sx={{ pl: 2, pr: 1 }}>
                    {onOpenWorkspaceMenu && (
                      <ListItem disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => onOpenWorkspaceMenu(accountRowRef.current)}
                          sx={{ py: 1.25, px: 1.5, borderRadius: 1, minHeight: 48 }}
                        >
                          <ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>
                            <SwapHorizIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Switch workspace" primaryTypographyProps={{ fontSize: "0.9375rem", ...menuItemTextProps.primaryTypographyProps }} />
                        </ListItemButton>
                      </ListItem>
                    )}
                    {onOpenCreateWorkspaceDialog && (
                      <ListItem disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          onClick={onOpenCreateWorkspaceDialog}
                          sx={{ py: 1.25, px: 1.5, borderRadius: 1, minHeight: 48 }}
                        >
                          <ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>
                            <AddIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Create new workspace" primaryTypographyProps={{ fontSize: "0.9375rem", ...menuItemTextProps.primaryTypographyProps }} />
                        </ListItemButton>
                      </ListItem>
                    )}
                    {onLogout && (
                      <ListItem disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => onLogout()}
                          sx={{ py: 1.25, px: 1.5, borderRadius: 1, minHeight: 48, color: "error.main" }}
                        >
                          <ListItemIcon sx={{ minWidth: 40, color: "error.main" }}>
                            <LogoutIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: "0.9375rem", ...menuItemTextProps.primaryTypographyProps }} />
                        </ListItemButton>
                      </ListItem>
                    )}
                  </List>
                </Collapse>
                <Divider sx={{ my: 1 }} />
              </>
            )}
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton component={Link} to={`/${workspaceSlug}/settings/workspace-information`} sx={{ py: 1.5, px: 1.5, borderRadius: 1, minHeight: 52 }}>
                <ListItemIcon sx={{ minWidth: 44, color: "primary.main" }}><BusinessIcon fontSize="medium" /></ListItemIcon>
                <ListItemText primary="Workspace" primaryTypographyProps={{ fontWeight: 600, fontSize: "1rem", ...menuItemTextProps.primaryTypographyProps }} />
                <ChevronRightIcon sx={{ color: "text.disabled", fontSize: 22 }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton component={Link} to={`/${workspaceSlug}/settings/members-permissions`} sx={{ py: 1.5, px: 1.5, borderRadius: 1, minHeight: 52 }}>
                <ListItemIcon sx={{ minWidth: 44, color: "primary.main" }}><PeopleOutlinedIcon fontSize="medium" /></ListItemIcon>
                <ListItemText primary="Members & permissions" primaryTypographyProps={{ fontWeight: 600, fontSize: "1rem", ...menuItemTextProps.primaryTypographyProps }} />
                <ChevronRightIcon sx={{ color: "text.disabled", fontSize: 22 }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton component={Link} to={`/${workspaceSlug}/settings/workspace-transactions`} sx={{ py: 1.5, px: 1.5, borderRadius: 1, minHeight: 52 }}>
                <ListItemIcon sx={{ minWidth: 44, color: "primary.main" }}><ReceiptLongIcon fontSize="medium" /></ListItemIcon>
                <ListItemText primary="Transactions" primaryTypographyProps={{ fontWeight: 600, fontSize: "1rem", ...menuItemTextProps.primaryTypographyProps }} />
                <ChevronRightIcon sx={{ color: "text.disabled", fontSize: 22 }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton component={Link} to={`/${workspaceSlug}/settings/workspace-preferences`} sx={{ py: 1.5, px: 1.5, borderRadius: 1, minHeight: 52 }}>
                <ListItemIcon sx={{ minWidth: 44, color: "primary.main" }}><SettingsOutlinedIcon fontSize="medium" /></ListItemIcon>
                <ListItemText primary="Preferences" primaryTypographyProps={{ fontWeight: 600, fontSize: "1rem", ...menuItemTextProps.primaryTypographyProps }} />
                <ChevronRightIcon sx={{ color: "text.disabled", fontSize: 22 }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={Link}
                to={`/${workspaceSlug}/settings/currencies`}
                sx={{ py: 1.5, px: 1.5, borderRadius: 1, minHeight: 52 }}
              >
                <ListItemIcon sx={{ minWidth: 44, color: "primary.main" }}>
                  <CurrencyExchangeIcon fontSize="medium" />
                </ListItemIcon>
                <ListItemText primary="Currencies" primaryTypographyProps={{ fontWeight: 600, fontSize: "1rem", ...menuItemTextProps.primaryTypographyProps }} />
                <ChevronRightIcon sx={{ color: "text.disabled", fontSize: 22 }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={Link}
                to={`/${workspaceSlug}/settings/accounts`}
                sx={{ py: 1.5, px: 1.5, borderRadius: 1, minHeight: 52 }}
              >
                <ListItemIcon sx={{ minWidth: 44, color: "primary.main" }}>
                  <AccountBalanceWalletIcon fontSize="medium" />
                </ListItemIcon>
                <ListItemText primary="Accounts" primaryTypographyProps={{ fontWeight: 600, fontSize: "1rem", ...menuItemTextProps.primaryTypographyProps }} />
                <ChevronRightIcon sx={{ color: "text.disabled", fontSize: 22 }} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={Link}
                to={`/${workspaceSlug}/settings/categories`}
                sx={{ py: 1.5, px: 1.5, borderRadius: 1, minHeight: 52 }}
              >
                <ListItemIcon sx={{ minWidth: 44, color: "primary.main" }}>
                  <CategoryIcon fontSize="medium" />
                </ListItemIcon>
                <ListItemText primary="Categories" primaryTypographyProps={{ fontWeight: 600, fontSize: "1rem", ...menuItemTextProps.primaryTypographyProps }} />
                <ChevronRightIcon sx={{ color: "text.disabled", fontSize: 22 }} />
              </ListItemButton>
            </ListItem>
            {isBypassAuthEnabled() && (
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton component={Link} to={`/${workspaceSlug}/settings/test`} sx={{ py: 1.5, px: 1.5, borderRadius: 1, minHeight: 52 }}>
                  <ListItemIcon sx={{ minWidth: 44, color: "text.secondary" }}>
                    <ScienceIcon fontSize="medium" />
                  </ListItemIcon>
                  <ListItemText primary="Test Page" primaryTypographyProps={{ fontWeight: 600, fontSize: "1rem", ...menuItemTextProps.primaryTypographyProps }} />
                  <ChevronRightIcon sx={{ color: "text.disabled", fontSize: 22 }} />
                </ListItemButton>
              </ListItem>
            )}
          </List>
          <Paper
            elevation={0}
            sx={{
              flexShrink: 0,
              borderTop: 1,
              borderColor: "divider",
              borderRadius: 0,
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "background.paper",
              minHeight: 48,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {formatVersion(getVersion())}
            </Typography>
            <Tooltip title="Hard Refresh (Clear Cache)">
              <IconButton
                size="small"
                onClick={() => {
                  const url = new URL(window.location.href);
                  url.searchParams.set("_refresh", Date.now().toString());
                  window.location.href = url.toString();
                }}
                sx={{ color: "text.secondary", "&:hover": { color: "primary.main", bgcolor: "action.hover" } }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Paper>
        </Box>
      );
    }
    // Manage Details and Select Details use full-page layout (like Create Transaction on mobile)
    if (isManageOrSelectDetailsPage) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, bgcolor: "background.default" }}>
          {currentPage === "manage-details" && (
            <ManageDetailsPage workspaceId={workspaceId} />
          )}
          {currentPage === "select-details" && (
            <SelectDetailsPage workspaceId={workspaceId} />
          )}
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "background.default", overflow: "hidden" }}>
        {currentPage !== "categories" && workspaceId ? <CategoriesEagerPreload workspaceId={workspaceId} /> : null}
        <Paper
          elevation={0}
          sx={{
            flexShrink: 0,
            borderBottom: 1,
            borderColor: "divider",
            borderRadius: 0,
            bgcolor: "background.paper",
            pt: "env(safe-area-inset-top, 0px)",
            minHeight: 56,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            px: 0,
            py: 0,
          }}
        >
          <IconButton
            component={Link}
            to={`/${workspaceSlug}/settings`}
            size="medium"
            sx={{
              color: "text.primary",
              minWidth: 48,
              minHeight: 48,
              "&:hover": { bgcolor: "action.hover" },
              "&:active": { bgcolor: "action.selected" },
            }}
            aria-label="Back to Settings"
          >
            <ArrowBackIcon sx={{ fontSize: 24 }} />
          </IconButton>
          <Typography
            variant="h6"
            fontWeight="600"
            sx={{
              flex: 1,
              fontSize: "1.125rem",
              pr: 1,
              minWidth: 0,
              whiteSpace: "normal",
              overflow: "visible",
              textOverflow: "unset",
            }}
          >
            {mobileSectionTitle[currentPage] || currentPage}
          </Typography>
        </Paper>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            p: "8px",
            bgcolor: "background.default",
          }}
        >
          {/* Render by currentPage so data loads correctly on mobile (nested Routes don't match full pathname) */}
          {isWorkspaceSectionPage(currentPage) && (
            <Box sx={{ mx: -2, px: 1.5, height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
              <WorkspaceSettings workspaceId={workspaceId} section={workspaceSectionFromPage(currentPage)} />
            </Box>
          )}
          {currentPage === "currencies" && (
            <Box sx={{ mx: -2, px: 1.5, height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
              <CurrencySettings workspaceId={workspaceId} />
            </Box>
          )}
          {currentPage === "accounts" && (
            <Box sx={{ mx: -2, px: 1.5, height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
              <AccountSettings workspaceId={workspaceId} />
            </Box>
          )}
          {currentPage === "categories" && (
            <Box sx={{ mx: -2, px: 1.5, height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
              <CategoriesGate workspaceId={workspaceId} />
            </Box>
          )}
          {currentPage === "members-permissions" && (
            <Box sx={{ mx: -2, px: 1.5, height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
              <MembersAndPermissionsSettings workspaceId={workspaceId} />
            </Box>
          )}
          {currentPage === "test" && (
            <Box sx={{ mx: -2, px: 1.5, height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
              <TestPage workspaceId={workspaceId} onTestTermsDialog={onTestTermsDialog} />
            </Box>
          )}
          {currentPage === "transaction-fields" && <Navigate to={`/${workspaceSlug}/settings/workspace-transactions`} replace />}
          {currentPage === "permissions" && <Navigate to={`/${workspaceSlug}/settings/members-permissions`} replace />}
          {currentPage === "workspace-danger" && <Navigate to={`/${workspaceSlug}/settings/workspace-information`} replace />}
          {!["workspace", "workspace-information", "workspace-transactions", "workspace-preferences", "currencies", "accounts", "categories", "manage-details", "select-details", "members-permissions", "transaction-fields", "permissions", "test"].includes(currentPage) && (
            <Navigate to={`/${workspaceSlug}/settings/workspace-information`} replace />
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", height: "100%", bgcolor: "background.default", overflow: "hidden" }}>
      {currentPage !== "categories" && workspaceId ? <CategoriesEagerPreload workspaceId={workspaceId} /> : null}
      {/* Left Sidebar */}
      <Drawer
        variant="persistent"
        open={true}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              borderRight: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              height: "100%",
              overflow: "visible",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
        <Box sx={{ pt: 2, pr: 2, pb: 1, pl: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            {/* Back to App Button - aligned with Settings icons */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0, flex: 1, minWidth: 0 }}>
              <Box
                component={Link}
                to={`/${workspaceSlug}/activity`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0,
                  textDecoration: "none",
                  color: "text.secondary",
                  "&:hover": {
                    color: "primary.main",
                    bgcolor: "action.hover",
                  },
                  borderRadius: 1,
                  px: 0,
                  py: 0.5,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    minWidth: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ArrowBackIcon fontSize="medium" />
                </Box>
                <Typography variant="body1" sx={{ ml: 0 }}>
                  Back to App
                </Typography>
              </Box>
            </Box>
          </Box>
          <Divider sx={{ mb: 1 }} />
        </Box>
        <List sx={{ pt: 0 }}>
          {(onOpenWorkspaceMenu || onOpenCreateWorkspaceDialog || onLogout) && (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  ref={accountRowRef}
                  onClick={() => setAccountExpanded((prev) => !prev)}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>
                    <BusinessIcon fontSize="medium" />
                  </ListItemIcon>
                  <ListItemText
                    primary={workspace?.name || "Account"}
                    primaryTypographyProps={{ fontWeight: 600, ...menuItemTextProps.primaryTypographyProps }}
                  />
                  <Box
                    component="span"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      transform: accountExpanded ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                      display: "inline-block",
                    }}
                  >
                    ▼
                  </Box>
                </ListItemButton>
              </ListItem>
              <Collapse in={accountExpanded} timeout="auto" unmountOnExit>
                <List disablePadding sx={{ pl: 2 }}>
                  {onOpenWorkspaceMenu && (
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => onOpenWorkspaceMenu(accountRowRef.current)}>
                        <ListItemIcon sx={{ minWidth: 40, color: "text.secondary" }}>
                          <SwapHorizIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Switch workspace" primaryTypographyProps={menuItemTextProps.primaryTypographyProps} />
                      </ListItemButton>
                    </ListItem>
                  )}
                  {onOpenCreateWorkspaceDialog && (
                    <ListItem disablePadding>
                      <ListItemButton onClick={onOpenCreateWorkspaceDialog}>
                        <ListItemIcon sx={{ minWidth: 40, color: "text.secondary" }}>
                          <AddIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Create new workspace" primaryTypographyProps={menuItemTextProps.primaryTypographyProps} />
                      </ListItemButton>
                    </ListItem>
                  )}
                  {onLogout && (
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => onLogout()}>
                        <ListItemIcon sx={{ minWidth: 40, color: "error.main" }}>
                          <LogoutIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Logout" primaryTypographyProps={{ color: "error.main", ...menuItemTextProps.primaryTypographyProps }} />
                      </ListItemButton>
                    </ListItem>
                  )}
                </List>
              </Collapse>
              <Divider sx={{ my: 1 }} />
            </>
          )}
          {(["workspace-information", "workspace-transactions", "workspace-preferences"] as const).map((page) => {
            const label = { "workspace-information": "Workspace", "workspace-transactions": "Transactions", "workspace-preferences": "Preferences" }[page];
            const Icon = { "workspace-information": BusinessIcon, "workspace-transactions": ReceiptLongIcon, "workspace-preferences": SettingsOutlinedIcon }[page];
            return (
              <React.Fragment key={page}>
                <ListItem disablePadding>
                  <ListItemButton
                    component={Link}
                    to={`/${workspaceSlug}/settings/${page}`}
                    selected={currentPage === page}
                    sx={{
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        "&:hover": { bgcolor: "primary.dark" },
                        "& .MuiListItemText-primary": { fontWeight: "bold" },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: currentPage === page ? "inherit" : "text.secondary" }}>
                      <Icon fontSize="medium" />
                    </ListItemIcon>
                    <ListItemText primary={label} primaryTypographyProps={menuItemTextProps.primaryTypographyProps} />
                  </ListItemButton>
                </ListItem>
                {page === "workspace-information" && (
                  <ListItem disablePadding>
                    <ListItemButton
                      component={Link}
                      to={`/${workspaceSlug}/settings/members-permissions`}
                      selected={currentPage === "members-permissions"}
                      sx={{
                        "&.Mui-selected": {
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          "&:hover": { bgcolor: "primary.dark" },
                          "& .MuiListItemText-primary": { fontWeight: "bold" },
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          color: currentPage === "members-permissions" ? "inherit" : "text.secondary",
                        }}
                      >
                        <PeopleOutlinedIcon fontSize="medium" />
                      </ListItemIcon>
                      <ListItemText primary="Members & permissions" primaryTypographyProps={menuItemTextProps.primaryTypographyProps} />
                    </ListItemButton>
                  </ListItem>
                )}
              </React.Fragment>
            );
          })}
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to={`/${workspaceSlug}/settings/currencies`}
              selected={currentPage === "currencies"}
              sx={{
                "&.Mui-selected": {
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                  "& .MuiListItemText-primary": {
                    fontWeight: "bold",
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: currentPage === "currencies" ? "inherit" : "text.secondary",
                }}
              >
                <CurrencyExchangeIcon fontSize="medium" />
              </ListItemIcon>
              <ListItemText primary="Currencies" primaryTypographyProps={menuItemTextProps.primaryTypographyProps} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to={`/${workspaceSlug}/settings/accounts`}
              selected={currentPage === "accounts"}
              sx={{
                "&.Mui-selected": {
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                  "& .MuiListItemText-primary": {
                    fontWeight: "bold",
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: currentPage === "accounts" ? "inherit" : "text.secondary",
                }}
              >
                <AccountBalanceWalletIcon fontSize="medium" />
              </ListItemIcon>
              <ListItemText primary="Accounts" primaryTypographyProps={menuItemTextProps.primaryTypographyProps} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to={`/${workspaceSlug}/settings/categories`}
              selected={currentPage === "categories"}
              sx={{
                "&.Mui-selected": {
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                  "& .MuiListItemText-primary": {
                    fontWeight: "bold",
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: currentPage === "categories" ? "inherit" : "text.secondary",
                }}
              >
                <CategoryIcon fontSize="medium" />
              </ListItemIcon>
              <ListItemText primary="Categories" primaryTypographyProps={menuItemTextProps.primaryTypographyProps} />
            </ListItemButton>
          </ListItem>
          {/* Test Page - only show in bypass auth mode (dev mode) */}
          {isBypassAuthEnabled() && (
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to={`/${workspaceSlug}/settings/test`}
                selected={currentPage === "test"}
                sx={{
                  "&.Mui-selected": {
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover": {
                      bgcolor: "primary.dark",
                    },
                    "& .MuiListItemText-primary": {
                      fontWeight: "bold",
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: currentPage === "test" ? "inherit" : "text.secondary",
                  }}
                >
                  <ScienceIcon fontSize="medium" />
                </ListItemIcon>
                <ListItemText primary="Test Page" primaryTypographyProps={menuItemTextProps.primaryTypographyProps} />
              </ListItemButton>
            </ListItem>
          )}
        </List>
        
        {/* Version Display and Refresh Button - Bottom Left */}
        <Box
          sx={{
            mt: "auto",
            p: 2,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.75rem",
              display: "block",
            }}
          >
            {formatVersion(getVersion())}
          </Typography>
          <Tooltip title="Hard Refresh (Clear Cache)">
            <IconButton
              size="small"
              onClick={() => {
                // Force hard refresh by adding timestamp to URL
                const url = new URL(window.location.href);
                url.searchParams.set('_refresh', Date.now().toString());
                window.location.href = url.toString();
              }}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "primary.main",
                  bgcolor: "action.hover",
                },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Dev Mode Selector - Only show in dev mode */}
        {isDevModeEnabled() && (
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <Card variant="outlined" sx={{ bgcolor: "background.paper" }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  🛠️ Development Mode
                </Typography>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend" sx={{ mb: 1, fontSize: "0.875rem" }}>
                    Testing Mode
                  </FormLabel>
                  <RadioGroup
                    value={devModeType}
                    onChange={(e) => {
                      const newType = e.target.value as DevModeType;
                      // Only update state, don't save to localStorage yet
                      // Save will happen when user clicks "Apply & Reload"
                      setDevModeTypeState(newType);
                    }}
                  >
                    <FormControlLabel
                      value="web-mobile"
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            Web + Mobile
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Bypass auth, auto-load Dev_Workspace (for mobile testing)
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="web-only"
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            Web Only
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Full authentication required (for testing permissions)
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
                <Alert severity="info" sx={{ mt: 2, fontSize: "0.75rem" }}>
                  <Typography variant="caption">
                    Changing mode requires page reload. Click "Apply & Reload" to restart with new settings.
                  </Typography>
                </Alert>
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      // Reset to web-mobile mode (safer default)
                      setDevModeType("web-mobile");
                      setDevModeTypeState("web-mobile");
                      // Clear any auth state
                      signOut(auth).catch(() => {});
                      // Reload
                      window.location.href = "/";
                    }}
                    sx={{ flex: 1 }}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={devModeType === currentDevModeType}
                    onClick={async () => {
                      // Save the selected mode to localStorage
                      setDevModeType(devModeType);
                      
                      if (devModeType === "web-only") {
                        // Switching to web-only mode - sign out and go to login
                        try {
                          await signOut(auth);
                        } catch (err) {
                          console.error("Error signing out:", err);
                        }
                        // Navigate to login page
                        window.location.href = "/login";
                      } else {
                        // Switching to web-mobile mode - clear workspace context and sign out
                        // This ensures bypass auth flow will trigger properly and load Dev_Workspace
                        clearWorkspaceContext();
                        try {
                          await signOut(auth);
                          // Small delay to ensure sign out completes
                          await new Promise(resolve => setTimeout(resolve, 100));
                        } catch (err) {
                          console.error("Error signing out:", err);
                        }
                        // Reload to trigger bypass auth flow which will auto-load Dev_Workspace
                        window.location.href = "/";
                      }
                    }}
                    sx={{ flex: 2 }}
                  >
                    Apply & Reload
                  </Button>
                </Stack>
                {devModeType === "web-only" && (
                  <Alert severity="warning" sx={{ mt: 1, fontSize: "0.75rem" }}>
                    <Typography variant="caption">
                      Make sure you're accessing via <strong>http://localhost:3000</strong> (not IP address) for authentication to work.
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </Drawer>

      {/* Main Content Area - pathname-based so sub-routes match when parent uses pathname routing */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        {atSettingsRoot && <Navigate to={`/${workspaceSlug}/settings/workspace-information`} replace />}
        {currentPage === "workspace" && <Navigate to={`/${workspaceSlug}/settings/workspace-information`} replace />}
        {isWorkspaceSectionPage(currentPage) && (
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <WorkspaceSettings workspaceId={workspaceId} section={workspaceSectionFromPage(currentPage)} />
          </Box>
        )}
        {currentPage === "currencies" && (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <CurrencySettings workspaceId={workspaceId} />
          </Box>
        )}
        {currentPage === "accounts" && (
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <AccountSettings workspaceId={workspaceId} />
          </Box>
        )}
        {currentPage === "categories" && (
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <CategoriesGate workspaceId={workspaceId} />
          </Box>
        )}
        {currentPage === "members-permissions" && (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <MembersAndPermissionsSettings workspaceId={workspaceId} />
          </Box>
        )}
        {currentPage === "transaction-fields" && <Navigate to={`/${workspaceSlug}/settings/workspace-transactions`} replace />}
        {currentPage === "permissions" && <Navigate to={`/${workspaceSlug}/settings/members-permissions`} replace />}
        {currentPage === "workspace-danger" && <Navigate to={`/${workspaceSlug}/settings/workspace-information`} replace />}
        {currentPage === "test" && (
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <TestPage workspaceId={workspaceId} onTestTermsDialog={onTestTermsDialog} />
          </Box>
        )}
        {!["workspace", "workspace-information", "workspace-transactions", "workspace-preferences", "currencies", "accounts", "categories", "members-permissions", "transaction-fields", "permissions", "test"].includes(currentPage) && !atSettingsRoot && (
          <Navigate to={`/${workspaceSlug}/settings/workspace-information`} replace />
        )}
      </Box>
    </Box>
  );
}

export default Settings;
